import React from 'react'
import Typography from 'aldoo-ra/Typography'
import Input from 'aldoo-ra/Input'
import ToggleSwitch from 'aldoo-ra/ToggleSwitch'
import DropdownSelect from 'aldoo-ra/DropdownSelect'
import PlatformSelector from 'aldoo-ra/PlatformSelector'
import { BeakerIcon, CurrencyDollarIcon, EnvelopeOpenIcon, UserGroupIcon, UserIcon, GlobeAltIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import CMSData from 'aldoo-ra/CMS/cms-data'

const LTV_OPERATORS = [
  { value: 'gte', label: 'More than or equal to' },
  { value: 'lte', label: 'Less than or equal to' }
]

const DELIVERY_OPERATORS = [
  { value: 'seen', label: 'Users who have seen' },
  { value: 'not_seen', label: 'Users who haven\'t seen' }
]

const FilteringControls = ({
  formData,
  errors,
  systemLanguages,
  platforms,
  loadingLanguages,
  basePlatformBreakdown,
  baseLanguageBreakdown,
  baseAudienceTotal,
  releases,
  loadingReleases,
  onInputChange,
  onLanguageChange,
  onPlatformChange,
  onDeliveryReleasesChange,
  getSelectedLanguages,
  getSelectedPlatforms,
  getSelectedReleases
}) => {
  // Fetch site name from CMS
  const { data: siteNameData } = CMSData({
    contentType: "Text",
    match: { "content.id": "site_name" },
  })

  const siteName = siteNameData?.[0]?.text || "Platform"
  // Enhance platforms with counts from base platform breakdown (without platform filters)
  const platformsWithCounts = React.useMemo(() => {
    if (!basePlatformBreakdown) {
      return platforms
    }

    const platformCounts = basePlatformBreakdown
    const anyCount = platformCounts['any'] || 0

    return platforms.map(platform => {
      // For "any" platform, show total audience size
      if (platform.value === 'any') {
        return {
          ...platform,
          label: `${platform.label} (${baseAudienceTotal.toLocaleString()})`
        }
      }

      // For specific platforms, include 'any' platform subscribers in the count
      // since they match all platform filters
      const specificCount = platformCounts[platform.value] || 0
      const totalCount = specificCount + anyCount

      return {
        ...platform,
        label: `${platform.label} (${totalCount.toLocaleString()})`
      }
    })
  }, [platforms, basePlatformBreakdown, baseAudienceTotal])

  // Enhance languages with counts from base language breakdown (without language filters)
  const languagesWithCounts = React.useMemo(() => {
    if (!baseLanguageBreakdown) {
      return systemLanguages
    }

    return systemLanguages.map(language => {
      // For "any" language, show total audience size
      if (language.value === 'any') {
        return {
          ...language,
          label: `${language.label} (${baseAudienceTotal.toLocaleString()})`
        }
      }

      // For specific languages, show their count
      const count = baseLanguageBreakdown[language.value] || 0

      return {
        ...language,
        label: `${language.label} (${count.toLocaleString()})`
      }
    })
  }, [systemLanguages, baseLanguageBreakdown, baseAudienceTotal])

  // Get selected platforms with counts
  const getSelectedPlatformsWithCounts = () => {
    return platformsWithCounts.filter(platform =>
      getSelectedPlatforms().some(selected => selected.value === platform.value)
    )
  }

  // Get selected languages with counts
  const getSelectedLanguagesWithCounts = () => {
    return languagesWithCounts.filter(language =>
      getSelectedLanguages().some(selected => selected.value === language.value)
    )
  }

  return (
    <div>
      <Typography component="h2" variant="admin_h4" className="mb-3">
        Filtering
      </Typography>

      <div className="space-y-3 mb-3">
        {/* Language Filter */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <GlobeAltIcon className="w-5 h-5 text-admin_primary flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  Language
                </Typography>

                <ToggleSwitch
                  isOn={formData.targetLanguagesEnabled}
                  onChange={(isOn) => {
                    onInputChange("targetLanguagesEnabled", isOn)
                    if (!isOn) {
                      onInputChange("targetLanguages", ["any"])
                    }
                  }}
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block mb-3">
                Select target languages for the newsletter
              </Typography>

              {formData.targetLanguagesEnabled && (
                <DropdownSelect
                  key={`languages-${languagesWithCounts.length}-${formData.targetLanguages.length}`}
                  variant="admin"
                  searchEnabled
                  multiSelection
                  showCheckbox={false}
                  showSelectionTick={true}
                  allowEmptySelection={false}
                  placeholder={
                    loadingLanguages
                      ? "Loading languages..."
                      : "Select languages..."
                  }
                  options={languagesWithCounts}
                  value={loadingLanguages ? [] : getSelectedLanguagesWithCounts()}
                  onChange={onLanguageChange}
                  fixedWidth="100%"
                  error={errors.targetLanguages}
                  loading={loadingLanguages}
                />
              )}
            </div>
          </div>
        </div>

        {/* Platform Filter */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <DevicePhoneMobileIcon className="w-5 h-5 text-admin_primary flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  Platform
                </Typography>

                <ToggleSwitch
                  isOn={formData.targetPlatformsEnabled}
                  onChange={(isOn) => {
                    onInputChange("targetPlatformsEnabled", isOn)
                    if (!isOn) {
                      onInputChange("targetPlatforms", ["any"])
                    }
                  }}
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block mb-3">
                Select target platforms for the newsletter
              </Typography>

              {formData.targetPlatformsEnabled && (
                <PlatformSelector
                  key={`platforms-${formData.targetPlatforms.length}`}
                  placeholder="Select platforms..."
                  platforms={platformsWithCounts}
                  value={getSelectedPlatformsWithCounts()}
                  onChange={onPlatformChange}
                  fixedWidth="100%"
                  error={errors.targetPlatforms}
                />
              )}
            </div>
          </div>
        </div>

        {/* Include Unregistered Subscribers */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <UserIcon className="w-5 h-5 text-admin_primary flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  Users with no {siteName} accounts
                </Typography>

                <ToggleSwitch
                  isOn={formData.includeUnregistered}
                  onChange={(isOn) =>
                    onInputChange("includeUnregistered", isOn)
                  }
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block">
                Include users without platform accounts
              </Typography>
            </div>
          </div>
        </div>

        {/* User LTV Filter */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <CurrencyDollarIcon className="w-5 h-5 text-admin_primary flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  LTV
                </Typography>

                <ToggleSwitch
                  isOn={formData.ltvFilterEnabled}
                  onChange={(isOn) => onInputChange("ltvFilterEnabled", isOn)}
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block mb-3">
                Filter users by their total life time value on the platform
              </Typography>

              {formData.ltvFilterEnabled && (
                <div className="grid grid-cols-2 gap-2">
                  <DropdownSelect
                    variant="admin"
                    searchEnabled={false}
                    multiSelection={false}
                    showCheckbox={false}
                    showSelectionTick={true}
                    placeholder="Operator"
                    options={LTV_OPERATORS}
                    value={
                      LTV_OPERATORS.find(
                        (op) => op.value === formData.ltvOperator,
                      )
                        ? [
                            LTV_OPERATORS.find(
                              (op) => op.value === formData.ltvOperator,
                            ),
                          ]
                        : []
                    }
                    onChange={(selected) =>
                      onInputChange("ltvOperator", selected[0]?.value || "gte")
                    }
                    fixedWidth="100%"
                  />

                  <Input
                    variant="admin"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.ltvAmount || ""}
                    onChange={(e) => onInputChange("ltvAmount", e.target.value)}
                    placeholder="Amount ($)"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User Delivery Filter */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <UserGroupIcon className="w-5 h-5 text-admin_primary flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  Delivery
                </Typography>

                <ToggleSwitch
                  isOn={formData.deliveryFilterEnabled}
                  onChange={(isOn) =>
                    onInputChange("deliveryFilterEnabled", isOn)
                  }
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block mb-3">
                Select users based on previous interactions with your newsletter
                releases
              </Typography>

              {formData.deliveryFilterEnabled && (
                <div className="space-y-2">
                  <DropdownSelect
                    variant="admin"
                    searchEnabled={false}
                    multiSelection={false}
                    showCheckbox={false}
                    showSelectionTick={true}
                    placeholder="Operator"
                    options={DELIVERY_OPERATORS}
                    value={
                      DELIVERY_OPERATORS.find(
                        (op) => op.value === formData.deliveryOperator,
                      )
                        ? [
                            DELIVERY_OPERATORS.find(
                              (op) => op.value === formData.deliveryOperator,
                            ),
                          ]
                        : []
                    }
                    onChange={(selected) =>
                      onInputChange(
                        "deliveryOperator",
                        selected[0]?.value || "not_seen",
                      )
                    }
                    fixedWidth="100%"
                  />

                  <DropdownSelect
                    variant="admin"
                    searchEnabled={true}
                    multiSelection={true}
                    showCheckbox={false}
                    showSelectionTick={true}
                    placeholder={
                      loadingReleases
                        ? "Loading releases..."
                        : "Select releases..."
                    }
                    options={releases || []}
                    value={getSelectedReleases()}
                    onChange={onDeliveryReleasesChange}
                    fixedWidth="100%"
                    loading={loadingReleases}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Audience Share */}
        <div className="bg-admin_canvas rounded-lg border border-admin_bordered p-4">
          <div className="flex items-start space-x-3">
            <BeakerIcon className="w-5 h-5 text-admin_warning flex-shrink-0" />

            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <Typography component="h2" variant="admin_h4" className="mb-0">
                  Audience Share
                </Typography>

                <ToggleSwitch
                  isOn={formData.audienceShareEnabled}
                  onChange={(isOn) => {
                    onInputChange("audienceShareEnabled", isOn)
                    if (!isOn) {
                      onInputChange("audienceShare", 100)
                    }
                  }}
                />
              </div>

              <Typography variant="admin_txt2_dim" className="block mb-3">
                Send to a percentage of the calculated audience.
              </Typography>

              {formData.audienceShareEnabled && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 max-w-32">
                    <Input
                      variant="admin"
                      type="number"
                      min="0.001"
                      max="100"
                      step="0.001"
                      value={formData.audienceShare || ""}
                      onChange={(e) => {
                        const inputValue = e.target.value

                        if (
                          inputValue === "" ||
                          inputValue === "0." ||
                          inputValue === "0.0" ||
                          inputValue === "0.00" ||
                          inputValue.endsWith(".")
                        ) {
                          onInputChange("audienceShare", inputValue)
                          return
                        }

                        const numValue = parseFloat(inputValue)

                        if (!isNaN(numValue)) {
                          onInputChange("audienceShare", inputValue)
                        }
                      }}
                      onBlur={() => {
                        const inputValue = formData.audienceShare

                        if (
                          inputValue === "" ||
                          inputValue === null ||
                          inputValue === undefined ||
                          inputValue === "0." ||
                          inputValue === "0" ||
                          inputValue === "0.0" ||
                          inputValue === "0.00"
                        ) {
                          onInputChange("audienceShare", 100)
                          return
                        }

                        const numValue = parseFloat(inputValue)
                        if (!isNaN(numValue) && numValue > 0) {
                          const constrainedValue = Math.min(
                            100,
                            Math.max(0.001, numValue),
                          )
                          onInputChange("audienceShare", constrainedValue)
                        } else {
                          onInputChange("audienceShare", 100)
                        }
                      }}
                      placeholder="100"
                      error={errors.audienceShare}
                    />
                  </div>

                  <Typography variant="admin_body_dim" className="my-0">
                    % of calculated audience
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FilteringControls
